/**
 * ModelDeployPanel — per-point model-deploy surface (step 8 of the ML retrain
 * pipeline). Shown on the "Компьютеры Linza → точка" detail page once the point
 * is DOCKER_RUNNING.
 *
 * Two independent install commands (each a separate host cron) + the run history:
 *   1. Обновление моделей — the model-update pull-cron (every point).
 *   2. Конвертация моделей — the TensorRT .plan builder cron (convert-host only).
 *   3. Cron-run history (last-run, rc, deployed versions, expandable log) from
 *      LocalComputerCronRun.
 */
import { Select } from '@/components/ui/select';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { useCallback, useEffect, useState } from 'react';

import {
  getCronRuns,
  getModelCronInstall,
  type CronRun,
} from '@/api/localComputers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { extractApiError } from '@/utils/apiError';

type T = (k: string, p?: Record<string, string | number>) => string;

/** Human-readable label for a model_deploy.sh exit code (see ops/model_deploy.sh).
 *  Falls back to "code N" for any rc the dictionary doesn't name. */
function rcLabel(rc: number, t: T): string {
  const key = `admin.lc.detail.modelDeploy.rc.${rc}`;
  const label = t(key);
  return label === key ? t('admin.lc.detail.modelDeploy.rc.unknown', { rc }) : label;
}

/** One "Показать команду установки" → copyable curl block, for a given install kind. */
function InstallCommand({
  lcId, what, mode, label, hint, t, onShown,
}: {
  lcId: string;
  what: 'deploy' | 'convert';
  mode: 'jetson' | 'host';
  label: string;
  hint: string;
  t: T;
  /** Called after the install command is fetched. For `what='convert'` the fetch
   *  also designates the point as a convert-host server-side. */
  onShown?: () => void;
}) {
  const [command, setCommand] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const show = () => {
    setBusy(true);
    setError(null);
    getModelCronInstall(lcId, what, mode)
      .then(resp => { setCommand(resp.data.install_command); onShown?.(); })
      .catch(e => setError(extractApiError(e, t('error.general'))))
      .finally(() => setBusy(false));
  };

  const copy = async () => {
    if (!command) return;
    await navigator.clipboard.writeText(command);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-1">
      <Button size="sm" variant="outline" onClick={show} disabled={busy}>
        {label}
      </Button>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {command && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{hint}</p>
          <div className="flex items-start gap-2">
            <pre className="flex-1 overflow-x-auto rounded bg-muted p-2 font-mono text-[11px] leading-relaxed text-foreground">
              {command}
            </pre>
            <Button size="sm" variant="ghost" onClick={copy}>
              {copied ? t('common.copied') : t('common.copy')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ModelDeployPanel(
  { lcId, isConvertHost, isDesignated = false }:
  { lcId: string; isConvertHost: boolean; isDesignated?: boolean },
) {
  const { t } = useT();
  const [runs, setRuns] = useState<CronRun[] | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [mode, setMode] = useState<'jetson' | 'host'>('jetson');
  const [openLog, setOpenLog] = useState<number | null>(null);
  // Server-side designation happens when the convert install command is fetched;
  // reflect it locally so the section updates without a full LC refetch.
  const [localDesignated, setLocalDesignated] = useState(false);
  const designated = isConvertHost || isDesignated || localDesignated;

  const loadRuns = useCallback(() => {
    setRunsError(null);
    getCronRuns(lcId)
      .then(resp => setRuns(resp.data.runs))
      .catch(e => setRunsError(extractApiError(e, t('error.general'))));
  }, [lcId, t]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  return (
    <Card variant="section">
      <CardContent className="space-y-6 pt-6">
        {/* ── Обновление моделей (every point) ── */}
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {t('admin.lc.detail.modelDeploy.title')}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('admin.lc.detail.modelDeploy.desc')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t('admin.lc.detail.modelDeploy.modeLabel')}:
            </span>
            <Select
              className="h-7 text-xs" wrapperClassName="w-auto"
              value={mode}
              onChange={e => setMode(e.target.value as 'jetson' | 'host')}
            >
              <option value="jetson">{t('admin.lc.detail.modelDeploy.modeJetson')}</option>
              <option value="host">{t('admin.lc.detail.modelDeploy.modeHost')}</option>
            </Select>
          </div>
          <InstallCommand
            key={mode}
            lcId={lcId}
            what="deploy"
            mode={mode}
            label={t('admin.lc.detail.modelDeploy.installBtn')}
            hint={t('admin.lc.detail.modelDeploy.installHint')}
            t={t}
          />
        </div>

        {/* ── Конвертация моделей — designate from here; the live flag flips
            only when the point's convert_drainer actually polls the backend. ── */}
        <div className="space-y-3 border-t border-border pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {t('admin.lc.detail.modelConvert.title')}
            </h3>
            {isConvertHost ? (
              <Badge variant="default">{t('admin.lc.detail.modelConvert.statusActive')}</Badge>
            ) : designated ? (
              <Badge variant="secondary">{t('admin.lc.detail.modelConvert.statusAwaiting')}</Badge>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('admin.lc.detail.modelConvert.desc')}
          </p>
          <InstallCommand
            lcId={lcId}
            what="convert"
            mode="jetson"
            label={t(designated
              ? 'admin.lc.detail.modelConvert.installBtn'
              : 'admin.lc.detail.modelConvert.designateBtn')}
            hint={t('admin.lc.detail.modelDeploy.installHint')}
            onShown={() => setLocalDesignated(true)}
            t={t}
          />
          {designated && !isConvertHost && (
            <p className="text-[11px] text-muted-foreground">
              {t('admin.lc.detail.modelConvert.awaitingHint')}
            </p>
          )}
        </div>

        {/* ── cron-run history ── */}
        <div className="space-y-2 border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground">
              {t('admin.lc.detail.modelDeploy.runs')}
            </span>
            <Button size="sm" variant="ghost" onClick={loadRuns}>
              {t('admin.lc.detail.modelDeploy.refresh')}
            </Button>
          </div>
          {runsError && !runs && <ServerStatusNotice variant="card" onRetry={loadRuns} />}
          {runs && runs.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('admin.lc.detail.modelDeploy.noRuns')}
            </p>
          )}
          {runs && runs.length > 0 && (
            <div className="space-y-1">
              {runs.map(r => (
                <div key={r.id} className="rounded border border-border p-2 text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={r.rc === 0 ? 'default' : 'destructive'}>
                      rc={r.rc} · {rcLabel(r.rc, t)}
                    </Badge>
                    <span className="text-muted-foreground">
                      {new Date(r.ran_at).toLocaleString()}
                    </span>
                    <span className="text-foreground">{r.summary}</span>
                    {Object.keys(r.deployed_versions || {}).length > 0 && (
                      <span className="text-muted-foreground">
                        {t('admin.lc.detail.modelDeploy.versions')}:{' '}
                        {Object.entries(r.deployed_versions)
                          .map(([n, v]) => `${n}=${String(v).slice(0, 8)}`)
                          .join(', ')}
                      </span>
                    )}
                    {r.log_tail && (
                      <button
                        type="button"
                        className="ml-auto text-primary underline"
                        onClick={() => setOpenLog(openLog === r.id ? null : r.id)}
                      >
                        {t('admin.lc.detail.modelDeploy.showLog')}
                      </button>
                    )}
                  </div>
                  {openLog === r.id && r.log_tail && (
                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[10px] leading-relaxed text-foreground">
                      {r.log_tail}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
